import { useState, useEffect, useCallback } from 'react'
import req from '@sportninja/common/api/request'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import type { FeatureFlags } from '@sportninja/common/types/FeatureFlags'

/**
 * Image data structure from API
 */
interface Image {
  id: string
  full_path: string
}

/**
 * Organization data structure from API
 */
interface Organization {
  id: string
  name: string
  name_full: string
  image?: Image | null
}

/**
 * Schedule/Competition data structure from API
 */
export interface Competition {
  id: string
  name: string
  name_full: string
  organization_id: string
  organization?: Organization
  image?: Image | null
  starts_at?: string
  ends_at?: string
  regulation_game_length?: number
}

/**
 * Competition option for dropdown
 */
export interface CompetitionOption {
  id: string
  name: string
  leagueName: string
  image?: string
}

/**
 * Hook result interface
 */
interface UseSchedulerCompetitionResult {
  /** Current competition data */
  competition: Competition | null
  /** List of competitions for dropdown */
  competitions: CompetitionOption[]
  /** Loading state for current competition */
  loading: boolean
  /** Loading state for competitions list */
  loadingList: boolean
  /** Error state */
  error: Error | null
  /** Fetch current competition */
  fetchCompetition: (id: string) => Promise<void>
  /** Fetch list of competitions */
  fetchCompetitions: () => Promise<void>
  /** Refetch current competition (e.g. after updating dates) */
  refetchCompetition: () => Promise<void>
  /** Feature flags from the schedule meta response */
  featureFlags: FeatureFlags | null
}

/**
 * Hook for fetching competition data for the scheduler wizard
 * @returns Object containing competition data, loading states, and fetch functions
 */
export const useSchedulerCompetition = (
  competitionId?: string
): UseSchedulerCompetitionResult => {
  const [competition, setCompetition] = useState<Competition | null>(null)
  const [competitions, setCompetitions] = useState<CompetitionOption[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingList, setLoadingList] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags | null>(null)

  /**
   * Fetches current competition details
   */
  const fetchCompetition = useCallback(async (id: string) => {
    if (!id) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await req(`/schedules/${id}`)
      setCompetition(response.data)
      setFeatureFlags(response.meta?.feature_flags ?? null)

      // Fetch schedule settings to get regulation_game_length
      try {
        const { data: settingsData } = await req(`/schedules/${id}/settings`)
        const regGameLength =
          settingsData?.game?.timing?.regulation_game_length ?? 60
        setCompetition((prev) =>
          prev ? { ...prev, regulation_game_length: regGameLength } : prev
        )
      } catch {
        // Settings fetch is best-effort; API-side enrichment is the safety net
      }
    } catch (err) {
      const errorObj =
        err instanceof Error ? err : new Error('Failed to fetch competition')
      setError(errorObj)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Fetches list of competitions the user has access to
   */
  const fetchCompetitions = useCallback(async () => {
    setLoadingList(true)
    setError(null)

    try {
      const { data } = await req('/users/me/schedules', {
        query: {
          per_page: 100,
          sort: 'starts_at',
          direction: 'desc',
        },
      })

      const options: CompetitionOption[] = data.map(
        (schedule: Competition & { organization?: Organization }) => ({
          id: schedule.id,
          name: schedule.name || schedule.name_full,
          leagueName: schedule.organization?.name || '',
          image: resolveAssetUrl(schedule.image),
        })
      )

      setCompetitions(options)
    } catch (err) {
      const errorObj =
        err instanceof Error ? err : new Error('Failed to fetch competitions')
      setError(errorObj)
    } finally {
      setLoadingList(false)
    }
  }, [])

  // Fetch competition on mount if competitionId is provided
  useEffect(() => {
    if (competitionId) {
      fetchCompetition(competitionId)
    }
  }, [competitionId, fetchCompetition])

  /**
   * Refetch the current competition (e.g. after updating dates)
   */
  const refetchCompetition = useCallback(async () => {
    if (competitionId) {
      await fetchCompetition(competitionId)
    }
  }, [competitionId, fetchCompetition])

  return {
    competition,
    competitions,
    loading,
    loadingList,
    error,
    fetchCompetition,
    fetchCompetitions,
    refetchCompetition,
    featureFlags,
  }
}

export default useSchedulerCompetition
